import { FC } from "react";
import { explode, formatNumber } from "src/utility";
import { useTranslation } from "react-i18next";
import { useJourneyStaticDetails } from "../../../../context";
import { useSSRContext } from "../../context/SSRContext";
import { generateMealOptions, getMealLabel } from "../../utils";
import { MealOption } from "src/Modules/Flights/constants/JourneyDetails";
import classNames from "classnames";
import { Button, Empty, Grid, Tag } from "antd";
import { PassengerSelectionTile } from "../PassengerSelection/components";
import { CheckSuccessIcon } from "src/assets/Icons";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { track } from "@amplitude/analytics-browser";

const MealItem: FC<{ meal: MealOption; onChange: any }> = ({ meal, onChange }) => {
  const { t } = useTranslation();
  const { getSSRItem, cursor } = useSSRContext();
  const mealValues = getSSRItem("meals", cursor.passengerId) as Record<string, MealOption>;
  const value = mealValues[meal.code];
  const selected = value && value.code === meal.code;
  const handleClick = () => {
    if (selected) {
      track("Meal Unselected", { ...meal });
      return onChange({});
    }
    onChange(meal);
    track("Meal Selected", {
      ...meal,
    });
  };
  const priceTag =
    meal.code === "NoMeal" ? null : meal?.price != 0 ? (
      `${formatNumber(meal.price)} ${meal.currency}`
    ) : (
      <Tag
        color="var(--color-font-success)"
        className="py-1.5! px-6! font-bold! rounded-sm! border-current! block! w-full! text-center! md:w-auto! md:inline-block! bg-background-default!"
      >
        {t("Flights.details.ssr.free")}
      </Tag>
    );
  return (
    <div
      onClick={handleClick}
      className={classNames(
        "border-border-minimal group flex h-full w-full min-w-min cursor-pointer flex-col justify-between gap-10 rounded-lg border bg-background-default p-5 shadow-sm transition-all duration-200 hover:shadow-md",
        selected && "bg-background-primary-lighter! border-border-active! shadow-none!",
      )}
    >
      <div className="flex items-start justify-between gap-3 font-semibold">
        <span>{getMealLabel(meal)}</span>
        <span className="border-border-minimal flex h-5 w-5 shrink-0 items-center justify-center rounded-full border">
          {selected && <CheckSuccessIcon className="h-full w-full text-icon-success" />}
        </span>
      </div>
      <div className="flex flex-col flex-col items-center justify-between gap-2 md:flex-row">
        <div className={classNames("text-font-active flex w-full flex-1 gap-1 text-base font-semibold")}>
          <span>{priceTag}</span>
        </div>
      </div>
    </div>
  );
};

export default function MealSelection() {
  const { cursor, flightOptions, updateSSRItem, passengerOptions } = useSSRContext();
  const { journeyDetails } = useJourneyStaticDetails();
  const { data } = explode(journeyDetails, {});
  const flightSelected = flightOptions.find((option) => option.value === cursor.flightId);
  const mealOptions = generateMealOptions(flightSelected, data?.ssr?.mealDynamic);
  const passenger = passengerOptions?.find((pass) => pass?.value === cursor?.passengerId);
  const isLargeScreen = Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];

  const handleMealChange = (code: string, value: MealOption) => {
    updateSSRItem("meals", Object.keys(value).length ? { [code]: value } : {});
  };

  const isEmpty = !mealOptions?.length;
  if (isEmpty)
    return (
      <div className="flex items-center justify-center">
        <Empty />
      </div>
    );

  return (
    <div className="flex flex-col gap-5 rounded-md bg-background-default p-4">
      {isLargeScreen && <PassengerSelectionTile passenger={passenger} isActive={true} />}
      <div className="grid w-full flex-1 auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 lg:gap-5">
        {mealOptions?.map((meal) => (
          <div key={`${meal.code}-${cursor.passengerId}-${flightSelected?.value}`} className="flex items-center">
            <MealItem meal={meal} onChange={(value: MealOption) => handleMealChange(meal.code, value)} />
          </div>
        ))}
      </div>
    </div>
  );
}
